/**
 * What a shared-item description edit asks (SPEC-christa-contacts-identity R5 AC4, replacing R3 AC4's all-or-nothing).
 *
 * A billable item can sit on several invoices. Rewording a line used to change the printed line on every live invoice, sent
 * and paid ones included. Now she is shown the other invoices, one tick box each, and only the ticked ones change. The
 * invoice she is editing always changes. A cancelled invoice is never offered (the edit never reaches it).
 *
 * Default ticks: unsent Draft and Ready invoices. Sent, partly paid and paid invoices are unticked because the client
 * already has the old wording. "Sent" follows the server's one send rule (`send_recorded`), so a Ready invoice that was
 * marked sent and moved back (invoice 0726) counts as sent here too.
 */
export interface OtherInvoiceOnItem {
  invoice_id: string;
  invoice_number?: string | null;
  status: string;
  send_recorded: boolean;
  payment_state?: 'paid' | 'partially_paid' | null;
  billed_amount?: number | null;
}

export type OtherInvoiceLabel = 'Draft' | 'Ready' | 'Sent' | 'Partly paid' | 'Paid';

export function otherInvoiceStatusLabel(other: OtherInvoiceOnItem): OtherInvoiceLabel {
  if (other.payment_state === 'paid' || other.status === 'paid') return 'Paid';
  if (other.payment_state === 'partially_paid' || other.status === 'partially_paid') return 'Partly paid';
  if (other.status === 'sent' || other.status === 'overdue' || other.send_recorded) return 'Sent';
  return other.status === 'ready' ? 'Ready' : 'Draft';
}

/** True for Sent, Partly paid and Paid rows: the client already has this invoice with the old wording. */
export function clientHasOldWording(other: OtherInvoiceOnItem): boolean {
  const label = otherInvoiceStatusLabel(other);
  return label === 'Sent' || label === 'Partly paid' || label === 'Paid';
}

/** Cancelled invoices are never offered. Anything the server sends with that status is dropped here as well. */
export function changeableOthers(others: OtherInvoiceOnItem[] | undefined | null): OtherInvoiceOnItem[] {
  return (others ?? []).filter((other) => other.status !== 'cancelled');
}

/** Unsent Draft and Ready invoices are ticked by default. */
export function defaultTickedIds(others: OtherInvoiceOnItem[] | undefined | null): string[] {
  return changeableOthers(others)
    .filter((other) => !clientHasOldWording(other))
    .map((other) => other.invoice_id);
}

/** k counts the edited invoice plus the ticked rows. */
export function changeButtonLabel(k: number): string {
  return k <= 1 ? 'Change this invoice only' : `Change ${k} invoices`;
}

export function otherInvoicesHint(n: number): string {
  return n === 1
    ? 'Also on 1 other invoice. You choose whether it changes.'
    : `Also on ${n} other invoices. You choose which ones change.`;
}

/**
 * The ids the server named when it refused a description edit with 409 `post_send_acknowledgement_required` (an invoice she
 * ticked, or the one she edited, became sent after the page loaded), or null when the error is anything else. The code can
 * sit under `error` (the app wraps HTTP errors), under `detail`, or at the top of the body.
 */
export function postSendAcknowledgementIds(error: unknown): string[] | null {
  const failure = error as { status?: number; data?: any } | null;
  if (!failure || failure.status !== 409) return null;
  const body = failure.data ?? {};
  const found = [body.error, body.detail, body].find(
    (part) => part && typeof part === 'object' && part.code === 'post_send_acknowledgement_required',
  );
  if (!found) return null;
  return Array.isArray(found.invoice_ids) ? found.invoice_ids.map(String) : [];
}

export interface DescriptionEditRequest {
  description: string;
  edited_invoice_id: string;
  description_invoice_ids: string[];
  /** The ticked invoices the client already has (sent, partly paid, paid). The server refuses a change to any of them without it. */
  acknowledge_sent_invoice_ids: string[];
}

/**
 * The request body: the invoice being edited, and exactly the ticked other invoices (an empty list when none).
 * `others` is the list she ticked from; every ticked row the client already has is acknowledged, because she saw
 * "The client already has the old wording." beside it and pressed the named Change button on purpose.
 */
export function descriptionEditRequest(
  description: string,
  editedInvoiceId: string,
  tickedIds: string[],
  others: OtherInvoiceOnItem[] = [],
  /** True when the edited invoice's own send evidence says sent (the server checks the edited invoice like a ticked one). */
  editedSendRecorded: boolean = false,
): DescriptionEditRequest {
  const ids = tickedIds.filter((id) => id !== editedInvoiceId);
  const old = new Set(others.filter(clientHasOldWording).map((other) => other.invoice_id));
  return {
    description,
    edited_invoice_id: editedInvoiceId,
    description_invoice_ids: ids,
    acknowledge_sent_invoice_ids: [...(editedSendRecorded ? [editedInvoiceId] : []), ...ids.filter((id) => old.has(id))],
  };
}
